// Inicio "Mi trabajo" data: GET /reports/inicio/resumen (see GET_INICIO in
// utils/Api.ts). While the backend does not have the endpoint (or it
// fails) the hook returns "error" and the page simply hides these blocks.
import { useEffect, useState } from "react";
import * as api from "../../utils/Api.ts";

export interface PendienteResumen {
  cantidad: number;
  // Oldest pending document date (ISO) or null
  masAntigua: string | null;
}

export interface DocumentoReciente {
  tipo: "SOC" | "OC";
  numero: number;
  fecha: string; // ISO, last update
  centrosCostoDestino: string[];
  montoClp: number;
  estado: { id: number; codigo: string; nombre: string };
}

export interface ProcesoConteo {
  solicitud: number;
  aprobacion: number;
  confirmacion: number;
  ordenCompra: number;
  recepcion: number;
  factura: number;
  pago: number;
}

export interface InicioResumen {
  pendientes: {
    solicitudesPorAprobar: PendienteResumen;
    solicitudesPorConfirmar: PendienteResumen;
    ordenesPorAprobar: PendienteResumen;
    ordenesPorConfirmar: PendienteResumen;
  };
  recientes: DocumentoReciente[];
  proceso: ProcesoConteo;
}

export type EstadoCarga =
  | { estado: "cargando" }
  | { estado: "ok"; datos: InicioResumen }
  | { estado: "error" };

const esResumen = (x: any): x is InicioResumen =>
  !!x &&
  typeof x === "object" &&
  !!x.pendientes &&
  Array.isArray(x.recientes) &&
  !!x.proceso;

/** Loads the summary once per user (initData from the login). */
export function useInicioResumen(initData: any): EstadoCarga {
  const [carga, setCarga] = useState<EstadoCarga>({ estado: "cargando" });

  useEffect(() => {
    if (!initData?.id) {
      setCarga({ estado: "error" });
      return;
    }
    let vigente = true;
    const roles: { codigo: string }[] = initData.roles ?? [];
    const tieneRol = (...codigos: string[]) =>
      roles.some((r) => codigos.includes(r.codigo));
    const conTexto = (v: unknown) => !!v && String(v).trim().length > 0;

    api.GET_INICIO.getResumen({
      idUser: initData.id,
      idCentroCosto: initData.id_centro_costo,
      centrosCostoVista: String(initData.centros_costo_vista ?? ""),
      // Same flags the approval / confirmation screens send
      rolesSoc: tieneRol("APRSOC", "CONFSOC"),
      rolesOc: tieneRol("APROC", "CONFOC"),
      subroganteSoc: conTexto(initData.mi_subrogancia_soc),
      subroganteOc: conTexto(initData.mi_subrogancia),
    })
      .then((raw: unknown) => {
        if (!vigente) return;
        setCarga(
          esResumen(raw) ? { estado: "ok", datos: raw } : { estado: "error" },
        );
      })
      .catch(() => {
        if (vigente) setCarga({ estado: "error" });
      });
    return () => {
      vigente = false;
    };
  }, [initData?.id]);

  return carga;
}

/** Whole days since an ISO date (null when unknown). */
export const diasDesde = (iso: string | null | undefined) => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / 86_400_000));
};

// State pill colors: same CSS variables as the maintenance tables
const COLOR_ESTADO: Record<string, string> = {
  PEND: "var(--color-pendiente)",
  ANUL: "var(--color-anuladas)",
  RECHP: "var(--color-rechazadas-parcial)",
  RECH: "var(--color-rechazadas)",
  APROP: "var(--color-aprobadas-parcial)",
  APRO: "var(--color-aprobadas)",
  CONFP: "var(--color-confirmadas-parcial)",
  CONF: "var(--color-confirmadas)",
  EMIT: "var(--color-emitidas)",
  PDGTE: "var(--color-pendientes-gte)",
  CFGTE: "var(--color-confirmadas-gte)",
};

export const colorEstado = (codigo: string | undefined) =>
  COLOR_ESTADO[
    String(codigo ?? "")
      .trim()
      .toUpperCase()
  ] ?? "var(--primary-color)";
